import { useEffect } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { z } from "zod";

import { useConfiguration, useUpdateConfiguration } from "@/api/configuration";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import SettingsCard from "./SettingsCard";

const schema = z.object({
  defaultCleansingFactor: z
    .number({ error: "Must be a number" })
    .min(0.1, "Must be between 0.1 and 5.0")
    .max(5.0, "Must be between 0.1 and 5.0"),
  cleansingFloorUnits: z
    .number({ error: "Must be a number" })
    .min(0, "Must be between 0 and 1000")
    .max(1000, "Must be between 0 and 1000"),
  cleansingMinHistoryMonths: z
    .number({ error: "Must be a number" })
    .int()
    .min(3, "Must be between 3 and 24 months")
    .max(24, "Must be between 3 and 24 months"),
});

type FormValues = z.infer<typeof schema>;

export default function CleansingSettings() {
  const { t } = useTranslation();
  const configQuery = useConfiguration();
  const update = useUpdateConfiguration();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      defaultCleansingFactor: 0.5,
      cleansingFloorUnits: 5.0,
      cleansingMinHistoryMonths: 6,
    },
  });

  useEffect(() => {
    if (configQuery.data) {
      form.reset({
        defaultCleansingFactor: configQuery.data.defaultCleansingFactor,
        cleansingFloorUnits: configQuery.data.cleansingFloorUnits,
        cleansingMinHistoryMonths: configQuery.data.cleansingMinHistoryMonths,
      });
    }
  }, [configQuery.data, form]);

  const onSubmit = (values: FormValues) => {
    update.mutate(values, {
      onSuccess: () =>
        toast.success(t("settings.saved", { defaultValue: "Saved" })),
      onError: () =>
        toast.error(t("settings.saveFailed", { defaultValue: "Save failed" })),
    });
  };

  return (
    <SettingsCard
      title={t("settings.rail.cleansing")}
      description={t("settings.configuration.cleansing.description")}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="defaultCleansingFactor"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("settings.configuration.cleansing.factorLabel")}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.1"
                    min={0.1}
                    max={5.0}
                    {...field}
                    onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  />
                </FormControl>
                <FormDescription>
                  {t("settings.configuration.cleansing.factorDescription")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cleansingFloorUnits"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("settings.configuration.cleansing.floorLabel")}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.5"
                    min={0}
                    max={1000}
                    {...field}
                    onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  />
                </FormControl>
                <FormDescription>
                  {t("settings.configuration.cleansing.floorDescription")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cleansingMinHistoryMonths"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("settings.configuration.cleansing.historyLabel")}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={3}
                    max={24}
                    {...field}
                    onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  />
                </FormControl>
                <FormDescription>
                  {t("settings.configuration.cleansing.historyDescription")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!form.formState.isDirty || update.isPending}
            >
              {update.isPending
                ? t("settings.configuration.saving")
                : t("common.buttons.save")}
            </Button>
          </div>
        </form>
      </Form>
    </SettingsCard>
  );
}
